import { useEffect, useState } from 'react';
import type {
  FilterBarController,
  FilterValues,
  UseFilterBarConfig,
} from '../types';
import { FilterBarStore } from '../state/store';

/**
 * Creates the FilterBar state machine and returns its controller.
 *
 * **The controller instance is identity-stable for the lifetime of the hook**
 * — safe to list in effect deps and capture in closures; calling this hook
 * never re-renders the caller by itself. Reactive reads go through
 * `useFilterBarSnapshot(controller)` / `useFilterBarSelector(controller,
 * selector)`; call-time reads through `controller.getState()` /
 * `getValues()` / `getItems()` / `getOptions()` / …; imperative reactions
 * outside render through `controller.subscribe()`.
 *
 * The hook's only jobs are (1) owning the store's lifetime, (2) syncing the
 * freshest config every render (callbacks, dataSource closures, persistence
 * adapter — the refs pattern), and (3) driving items reconciliation,
 * controlled-values sync, and the restart-safe init lifecycle from effects
 * (StrictMode's mount → cleanup → mount aborts in-flight init fetches and
 * re-enters init on the second mount).
 */
export function useFilterBar<T extends FilterValues = FilterValues>(
  config: UseFilterBarConfig<T>,
): FilterBarController<T> {
  // Created exactly once; the constructor is side-effect-free (no fetches),
  // so a StrictMode-discarded instance leaks nothing.
  const [store] = useState(() => new FilterBarStore<T>(config));

  // Refs-style sync — the orchestrator must always see the freshest config
  // without re-running anything.
  store.syncConfig(config);

  const { items, values: controlledValues } = config;

  // Items reconciliation — structural changes & static options content.
  // Reference-only changes with equal content are a no-op inside the store.
  useEffect(() => {
    store.reconcileItems(items);
  }, [store, items]);

  // Controlled-mode prop sync (echo-silent diff).
  useEffect(() => {
    if (controlledValues) store.applyControlled(controlledValues);
  }, [store, controlledValues]);

  // Init lifecycle — restart-safe.
  useEffect(() => {
    store.start();
    return () => store.stop();
  }, [store]);

  return store;
}

// Public re-export of helpers exported by the hook module historically.
export { shallowEqualValue, shallowEqualValues } from '../state/init-sequencer';
